import { useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Table from "react-bootstrap/Table";
import Form from "react-bootstrap/Form";
import { Button } from "react-bootstrap";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";

import { DasboardContext } from "../../context/dashboard-context";
import Breadcrumb from "../Breadcrumb/Breadcrumb";
import "../VitalDetail/style.css";

const API_BASE = "https://app.vivasmartcare.com/api";
const ROWS_PER_PAGE = 30;
const ALARMS_PER_PAGE = 20;

// Hobacare sleep stage status → tablo etiketi (en_US Cookie ile geliyor)
const SLEEP_STAGE_MAP = {
  "0": "Derin Uyku",
  "1": "Hafif Uyku",
  "2": "Uyanık",
  "3": "Yatak Dışı",
  "4": "Çevrimdışı",
  "7": "Göz Hareketi",
};

// alarmEvents.eventType kodları (doc 5.5.1)
const ALARM_EVENT_TYPE_MAP = {
  11: "Hızlı Solunum",
  12: "Yüzeysel Solunum",
  13: "Solunum Durması",
  14: "Yüksek Kalp Atışı",
  15: "Düşük Kalp Atışı",
};

// AlarmLog eventType → TR etiket (yerel Hobacare alarmları)
const LOCAL_ALARM_EVENT_MAP = {
  "Enter room": "Odaya Girdi",
  "Leave room": "Odadan Çıktı",
  "Enter area": "Yatağa Girdi",
  "Leave area": "Yataktan Çıktı",
  "Suspected fall": "Şüpheli Düşme",
  "Fall confirmation": "Düşme Onayı",
  "Heart rate abnormal": "Anormal Kalp Atışı",
  "Breath rate abnormal": "Anormal Solunum",
};

function fmtDate(d) {
  return d.toLocaleString("tr-TR", {
    timeZone: "Europe/Istanbul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function localDateString(d) {
  // "yyyy-MM-dd" (yerel saatte)
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function enumerateDays(startStr, endStr) {
  // datetime-local string'i → günleri yyyy-MM-dd dizisi olarak listele
  const start = new Date(startStr);
  const end = new Date(endStr);
  if (isNaN(start) || isNaN(end) || start > end) return [];
  const days = [];
  const cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  while (cur <= last) {
    days.push(localDateString(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return days;
}

// Bir günün raporundaki timestamps[] HH:MM:SS dizisini tam Date objelerine çevirir.
// Uyku gece yarısını geçtiğinde gün rolling yapılır.
function buildDataItems(report, date) {
  const ts = report.timestamps || [];
  const br = report.breathRateVo?.dataList || [];
  const hr = report.heartRateVo?.dataList || [];
  const stages = report.statisticalData || [];

  // İlk timestamp'ten itibaren günü takip et
  // getBedIdx genelde "yyyy-MM-dd HH:MM:SS" formatında — başlangıç günü
  let baseDate;
  if (report.getBedIdx && /^\d{4}-\d{2}-\d{2} /.test(report.getBedIdx)) {
    baseDate = new Date(report.getBedIdx.replace(" ", "T"));
    baseDate = new Date(
      baseDate.getFullYear(),
      baseDate.getMonth(),
      baseDate.getDate()
    );
  } else {
    // Fallback: rapor tarihinin bir gün öncesi (uyku önceki akşam başlar)
    const [y, m, d] = date.split("-").map(Number);
    baseDate = new Date(y, m - 1, d - 1);
  }

  // Uyku evrelerini hızlı lookup için Date aralıkları hâlinde tut
  const stageRanges = stages
    .map((s) => {
      const start = s.startTime ? new Date(s.startTime.replace(" ", "T")) : null;
      const end = s.endTime ? new Date(s.endTime.replace(" ", "T")) : null;
      return start && end ? { start, end, status: s.status } : null;
    })
    .filter(Boolean);

  const findStage = (date) => {
    for (const r of stageRanges) {
      if (date >= r.start && date < r.end) return r.status;
    }
    return null;
  };

  const items = [];
  let prevHour = -1;
  let dayOffset = 0;
  for (let i = 0; i < ts.length; i++) {
    const parts = String(ts[i]).split(":").map(Number);
    if (parts.length < 2 || parts.some(isNaN)) continue;
    const [hh, mm, ss = 0] = parts;
    if (prevHour !== -1 && hh < prevHour - 2) {
      // Saat geri sardıysa gün ilerlemiş
      dayOffset++;
    }
    prevHour = hh;
    const itemDate = new Date(baseDate);
    itemDate.setDate(itemDate.getDate() + dayOffset);
    itemDate.setHours(hh, mm, ss, 0);

    const stage = findStage(itemDate);
    items.push({
      time: itemDate.toISOString(),
      hr: hr[i] ? Number(hr[i]) : null,
      rr: br[i] ? Number(br[i]) : null,
      sleepStage: stage,
    });
  }
  return items;
}

function extractAlarmEvents(report) {
  if (!report?.alarmEvents) return [];
  const events = Array.isArray(report.alarmEvents)
    ? report.alarmEvents
    : Object.values(report.alarmEvents);
  return events.map((e) => ({
    time: e.ts ? new Date(e.ts.replace(" ", "T")).toISOString() : null,
    eventType: e.eventType,
    label: ALARM_EVENT_TYPE_MAP[e.eventType] || e.eventName || "Bilinmeyen Olay",
    source: "hobacare_report",
  }));
}

const HobacareVitalDetail = () => {
  const { sidebarOpen, bedData, userInfo } = useContext(DasboardContext);
  const { id } = useParams();

  const [thisDevice, setThisDevice] = useState({});
  const [startDatetime, setStartDatetime] = useState("");
  const [endDatetime, setEndDatetime] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasQueried, setHasQueried] = useState(false);

  const [vitalItems, setVitalItems] = useState([]);
  const [reportAlarms, setReportAlarms] = useState([]);
  const [dailySummaries, setDailySummaries] = useState([]);
  const [localAlarms, setLocalAlarms] = useState([]);
  const [warnings, setWarnings] = useState([]);

  const [vitalPage, setVitalPage] = useState(1);
  const [alarmPage, setAlarmPage] = useState(1);

  useEffect(() => {
    if (!bedData || bedData.length === 0) return;
    const found = bedData.find((item) => item.deviceID === id);
    setThisDevice(found || {});
  }, [bedData, id]);

  const datesValid = useMemo(() => {
    if (!startDatetime || !endDatetime) return false;
    return new Date(startDatetime) <= new Date(endDatetime);
  }, [startDatetime, endDatetime]);

  const fetchData = async () => {
    setLoading(true);
    setHasQueried(true);
    setWarnings([]);
    try {
      const days = enumerateDays(startDatetime, endDatetime);
      // 14 günden eski tarihleri uyarıyla filtrele
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const cutoff = new Date(today.getTime() - 14 * 86400000);
      const acceptedDays = [];
      const skippedDays = [];
      for (const d of days) {
        const [y, m, dd] = d.split("-").map(Number);
        const dt = new Date(y, m - 1, dd);
        if (dt < cutoff) skippedDays.push(d);
        else acceptedDays.push(d);
      }
      if (skippedDays.length > 0) {
        setWarnings((w) => [
          ...w,
          `Sistem 14 günden eski veriyi saklamıyor. Atlanılan günler: ${skippedDays.join(", ")}`,
        ]);
      }

      const startMs = new Date(startDatetime).getTime();
      const endMs = new Date(endDatetime).getTime();

      const allItems = [];
      const allAlarms = [];
      const summaries = [];

      for (const date of acceptedDays) {
        try {
          const r = await fetch(
            `${API_BASE}/hobacare/sleep-report?uid=${encodeURIComponent(
              id
            )}&date=${date}`
          );
          const j = await r.json();
          if (String(j.code) !== "200") {
            setWarnings((w) => [
              ...w,
              `${date}: ${j.msg || "veri alınamadı"}`,
            ]);
            continue;
          }
          const report = Array.isArray(j.data) ? j.data[0] : j.data;
          if (!report) continue;

          // Günlük özet
          summaries.push({
            date,
            score: report.score ?? "-",
            sleepTotalTime: report.sleepTotalTime ?? "-",
            onBedTime: report.onBedTime ?? "-",
            leaveBedCount: report.leaveBedCount ?? "-",
            scoreLabel: report.scoreLabel || "-",
            ahi: report.ahi || "-",
            breathAvg: report.breathRateVo?.avg ?? "-",
            breathMin: report.breathRateVo?.min ?? "-",
            breathMax: report.breathRateVo?.max ?? "-",
            heartAvg: report.heartRateVo?.avg ?? "-",
            heartMin: report.heartRateVo?.min ?? "-",
            heartMax: report.heartRateVo?.max ?? "-",
          });

          // Vital satırları
          const dayItems = buildDataItems(report, date);
          allItems.push(...dayItems);

          // Apnea / abnormal alarmlar
          allAlarms.push(...extractAlarmEvents(report));
        } catch (e) {
          setWarnings((w) => [...w, `${date}: ${e.message}`]);
        }
      }

      // Tarih aralığına göre filtrele (gün bazlı çekiyoruz ama saat hassasiyetinde keseriz)
      const filteredItems = allItems
        .filter((it) => {
          const t = new Date(it.time).getTime();
          return t >= startMs && t <= endMs;
        })
        .sort((a, b) => new Date(a.time) - new Date(b.time));

      const filteredAlarms = allAlarms
        .filter((a) => {
          if (!a.time) return true;
          const t = new Date(a.time).getTime();
          return t >= startMs && t <= endMs;
        })
        .sort((a, b) => new Date(b.time) - new Date(a.time));

      setVitalItems(filteredItems);
      setReportAlarms(filteredAlarms);
      setDailySummaries(summaries);
      setVitalPage(1);
      setAlarmPage(1);

      // Lokal AlarmLog'tan da Hobacare alarmlarını çek (oda giriş/çıkış vb.)
      await fetchLocalAlarms();
    } catch (error) {
      console.error("Hobacare veri çekme hatası:", error);
      setWarnings((w) => [...w, error.message]);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocalAlarms = async () => {
    try {
      const params = new URLSearchParams({
        start: new Date(startDatetime).toISOString(),
        end: new Date(endDatetime).toISOString(),
        resolved: "false",
        limit: "200",
      });
      if (userInfo?.company) params.append("company", userInfo.company);
      params.append("userDevices", JSON.stringify([id]));
      const r = await fetch(`${API_BASE}/alarm-logs?${params}`);
      const j = await r.json();
      const onlyHoba = (j.data || []).filter(
        (a) => a.alarmScenario === "hobacare" && a.deviceId === id
      );
      setLocalAlarms(onlyHoba);

      // resolved=true tarafından da al (geçmiş)
      const params2 = new URLSearchParams({
        start: new Date(startDatetime).toISOString(),
        end: new Date(endDatetime).toISOString(),
        resolved: "true",
        limit: "200",
      });
      if (userInfo?.company) params2.append("company", userInfo.company);
      params2.append("userDevices", JSON.stringify([id]));
      const r2 = await fetch(`${API_BASE}/alarm-logs?${params2}`);
      const j2 = await r2.json();
      const resolvedHoba = (j2.data || []).filter(
        (a) => a.alarmScenario === "hobacare" && a.deviceId === id
      );
      setLocalAlarms((prev) => {
        const seen = new Set(prev.map((a) => a._id));
        return [...prev, ...resolvedHoba.filter((a) => !seen.has(a._id))].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
      });
    } catch (e) {
      console.error("Local AlarmLog çekme hatası:", e);
    }
  };

  const handleQuery = () => {
    if (!datesValid) return;
    fetchData();
  };

  // Birleşik alarm listesi: rapor alarmları + lokal alarmlar
  const combinedAlarms = useMemo(() => {
    const reportRows = reportAlarms.map((a) => ({
      time: a.time,
      label: a.label,
      source: "Cihaz Raporu",
    }));
    const localRows = localAlarms.map((a) => ({
      time: a.endTimestamp || a.createdAt,
      label: LOCAL_ALARM_EVENT_MAP[a.eventType] || a.eventType,
      source: "Sistem Logu",
      description: a.description,
    }));
    return [...reportRows, ...localRows].sort(
      (a, b) => new Date(b.time) - new Date(a.time)
    );
  }, [reportAlarms, localAlarms]);

  const vitalTotalPages = Math.max(1, Math.ceil(vitalItems.length / ROWS_PER_PAGE));
  const vitalPageData = useMemo(() => {
    const i = (vitalPage - 1) * ROWS_PER_PAGE;
    return vitalItems.slice(i, i + ROWS_PER_PAGE);
  }, [vitalItems, vitalPage]);

  const alarmTotalPages = Math.max(1, Math.ceil(combinedAlarms.length / ALARMS_PER_PAGE));
  const alarmPageData = useMemo(() => {
    const i = (alarmPage - 1) * ALARMS_PER_PAGE;
    return combinedAlarms.slice(i, i + ALARMS_PER_PAGE);
  }, [combinedAlarms, alarmPage]);

  const downloadExcel = () => {
    if (vitalItems.length === 0) return;
    const headers = ["Zaman", "Nabız (bpm)", "Solunum (rpm)", "Uyku Durumu"];
    const rows = vitalItems.map((it) => [
      fmtDate(new Date(it.time)),
      it.hr ?? "",
      it.rr ?? "",
      SLEEP_STAGE_MAP[it.sleepStage] || "",
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `vital-${id}.csv`;
    a.click();
  };

  const downloadPDF = () => {
    if (vitalItems.length === 0) return;
    const doc = new jsPDF("l", "pt", "a4");
    doc.setFontSize(14);
    doc.text(`Vital Rapor - ${id}`, 40, 40);
    doc.setFontSize(9);
    doc.text(
      `Tarih: ${new Date().toLocaleString("tr-TR")} | Toplam: ${vitalItems.length} kayit`,
      40,
      55
    );

    autoTable(doc, {
      head: [["Zaman", "Nabiz (bpm)", "Solunum (rpm)", "Uyku Durumu"]],
      body: vitalItems.map((it) => [
        fmtDate(new Date(it.time)),
        it.hr ?? "-",
        it.rr ?? "-",
        SLEEP_STAGE_MAP[it.sleepStage] || "-",
      ]),
      startY: 70,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [22, 160, 133] },
    });
    doc.save(`vital-${id}.pdf`);
  };

  return (
    <>
      <Breadcrumb />
      <div
        className={`device-wrapper component-wrapper ${
          sidebarOpen ? "" : "close"
        }`}
      >
        <div className="vital-info">
          <p>
            Oda Adı: <strong>{thisDevice.roomName || "-"}</strong>
          </p>
          <p>|</p>
          <p>
            Süit Adı: <strong>{thisDevice.suiteName || "-"}</strong>
          </p>
        </div>

        <div className="date-picker-container">
          <div>
            <label>Başlangıç Tarihi ve Saati:</label>
            <Form.Control
              type="datetime-local"
              value={startDatetime}
              onChange={(e) => setStartDatetime(e.target.value)}
              max={endDatetime || undefined}
            />
          </div>
          <div>
            <label>Bitiş Tarihi ve Saati:</label>
            <Form.Control
              type="datetime-local"
              value={endDatetime}
              onChange={(e) => setEndDatetime(e.target.value)}
              min={startDatetime || undefined}
            />
          </div>
          <div style={{ display: "flex", alignItems: "flex-end" }}>
            <Button
              variant="primary"
              onClick={handleQuery}
              disabled={!datesValid || loading}
            >
              {loading ? "Yükleniyor..." : "Sorgula"}
            </Button>
          </div>
          {startDatetime && endDatetime && !datesValid && (
            <p style={{ color: "#e74c3c", marginTop: "10px" }}>
              ⚠️ Başlangıç tarihi bitiş tarihinden sonra olamaz!
            </p>
          )}
        </div>

        {warnings.length > 0 && (
          <div
            style={{
              background: "#fff3cd",
              border: "1px solid #ffeeba",
              color: "#856404",
              borderRadius: "8px",
              padding: "10px 14px",
              marginBottom: "16px",
              fontSize: "0.9rem",
            }}
          >
            {warnings.map((w, i) => (
              <div key={i}>⚠️ {w}</div>
            ))}
          </div>
        )}

        <div className="download-wrapper">
          <Button
            className="download-btn"
            onClick={downloadExcel}
            disabled={vitalItems.length === 0}
          >
            Verileri İndir (Excel)
          </Button>
          <Button
            className="download-btn"
            onClick={downloadPDF}
            disabled={vitalItems.length === 0}
          >
            Verileri İndir (PDF)
          </Button>
        </div>

        <Tabs defaultActiveKey="vital" className="mb-3">
          <Tab eventKey="vital" title="Vital Değerler">
            {dailySummaries.length > 0 && (
              <div style={{ marginBottom: "20px" }}>
                <h6>Günlük Özet</h6>
                <Table striped bordered hover size="sm" responsive>
                  <thead>
                    <tr>
                      <th>Tarih</th>
                      <th>Uyku Skoru</th>
                      <th>Toplam Uyku (dk)</th>
                      <th>Yatakta (dk)</th>
                      <th>Yataktan Kalkma</th>
                      <th>AHI</th>
                      <th>Nabız (min/avg/max)</th>
                      <th>Solunum (min/avg/max)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dailySummaries.map((s) => (
                      <tr key={s.date}>
                        <td>{s.date}</td>
                        <td>{s.score}</td>
                        <td>{s.sleepTotalTime}</td>
                        <td>{s.onBedTime}</td>
                        <td>{s.leaveBedCount}</td>
                        <td>{s.ahi}</td>
                        <td>
                          {s.heartMin}/{s.heartAvg}/{s.heartMax}
                        </td>
                        <td>
                          {s.breathMin}/{s.breathAvg}/{s.breathMax}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}

            {hasQueried && vitalItems.length === 0 && !loading && (
              <p style={{ color: "#999" }}>
                Seçilen aralıkta vital veri bulunamadı.
              </p>
            )}

            {vitalItems.length > 0 && (
              <>
                <Table striped bordered hover responsive className="alarm-table">
                  <thead>
                    <tr>
                      <th>Zaman</th>
                      <th>Nabız (bpm)</th>
                      <th>Solunum (rpm)</th>
                      <th>Uyku Durumu</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vitalPageData.map((it, i) => (
                      <tr key={i}>
                        <td>{fmtDate(new Date(it.time))}</td>
                        <td>{it.hr ?? "-"}</td>
                        <td>{it.rr ?? "-"}</td>
                        <td>{SLEEP_STAGE_MAP[it.sleepStage] || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <div className="pagination-controls">
                  <Button
                    onClick={() => setVitalPage((p) => Math.max(1, p - 1))}
                    disabled={vitalPage === 1}
                  >
                    Önceki
                  </Button>
                  <span>
                    {vitalPage} / {vitalTotalPages} ({vitalItems.length} kayıt)
                  </span>
                  <Button
                    onClick={() =>
                      setVitalPage((p) => Math.min(vitalTotalPages, p + 1))
                    }
                    disabled={vitalPage >= vitalTotalPages}
                  >
                    Sonraki
                  </Button>
                </div>
              </>
            )}
          </Tab>

          <Tab eventKey="alarms" title="Alarm Bilgileri">
            {hasQueried && combinedAlarms.length === 0 && !loading && (
              <p style={{ color: "#999" }}>
                Seçilen aralıkta alarm bulunamadı.
              </p>
            )}
            {combinedAlarms.length > 0 && (
              <>
                <Table striped bordered hover responsive className="alarm-table">
                  <thead>
                    <tr>
                      <th>Sıra</th>
                      <th>Zaman</th>
                      <th>Olay</th>
                      <th>Kaynak</th>
                      <th>Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    {alarmPageData.map((a, i) => (
                      <tr key={i}>
                        <td>{(alarmPage - 1) * ALARMS_PER_PAGE + i + 1}</td>
                        <td>{a.time ? fmtDate(new Date(a.time)) : "-"}</td>
                        <td>{a.label}</td>
                        <td>{a.source}</td>
                        <td>{a.description || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <div className="pagination-controls">
                  <Button
                    onClick={() => setAlarmPage((p) => Math.max(1, p - 1))}
                    disabled={alarmPage === 1}
                  >
                    Önceki
                  </Button>
                  <span>
                    {alarmPage} / {alarmTotalPages} ({combinedAlarms.length}{" "}
                    kayıt)
                  </span>
                  <Button
                    onClick={() =>
                      setAlarmPage((p) => Math.min(alarmTotalPages, p + 1))
                    }
                    disabled={alarmPage >= alarmTotalPages}
                  >
                    Sonraki
                  </Button>
                </div>
              </>
            )}
          </Tab>
        </Tabs>
      </div>
    </>
  );
};

export default HobacareVitalDetail;
